<template>
  <v-container fluid>
    <div class="d-flex align-center mb-4">
      <PageHeader class="flex-grow-1" title="Seguimiento comercial" />
      <v-chip small class="ml-3" :color="isAdmin ? 'indigo' : 'blue-grey'" dark>
        <v-icon left small>{{ isAdmin ? 'mdi-shield-account' : 'mdi-account' }}</v-icon>
        {{ isAdmin ? 'Administrador' : 'Usuario' }}
      </v-chip>
      <v-btn v-if="isAdmin" color="grey darken-1" text class="ml-3" @click="openParams">
        <v-icon left>mdi-tune</v-icon>Parámetros
      </v-btn>
      <v-btn v-if="isAdmin" color="grey darken-1" text class="ml-2" @click="openRealCosts">
        <v-icon left>mdi-cash-edit</v-icon>Costos reales
      </v-btn>
      <v-btn color="primary" depressed class="ml-2" @click="openContact">
        <v-icon left>mdi-phone-plus</v-icon>Registrar contacto
      </v-btn>
      <v-btn color="teal" dark depressed class="ml-2" @click="openDeal">
        <v-icon left>mdi-handshake</v-icon>Registrar negocio
      </v-btn>
    </div>

    <!-- Filtro -->
    <v-card class="mb-4">
      <v-card-text>
        <v-row>
          <v-col cols="12" md="3">
            <v-select
              v-model="salesperson" :items="salespeopleItems" label="Vendedor"
              outlined hide-details clearable @change="loadActivity"
              no-data-text="Aún no hay vendedores — registra un contacto primero"
            ></v-select>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- KPIs -->
    <v-row dense class="mb-2">
      <v-col cols="6" md="3" lg="2" v-for="k in kpis" :key="k.label">
        <v-card class="pa-3 text-center">
          <div class="text-caption text--secondary">{{ k.label }}</div>
          <div class="text-h6 font-weight-bold" :class="k.color">{{ k.value }}</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Actividad comercial -->
    <v-card class="mb-2">
      <v-card-title class="text-subtitle-1 font-weight-bold">Actividad de contactos</v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="8">
            <div class="text-center font-weight-medium mb-1">Contactos por semana
              <span class="text-caption text--secondary">· meta {{ settings.weekly_contact_goal }}/sem</span>
            </div>
            <div style="position: relative; height: 240px; width: 100%;">
              <Bar :chart-data="activityWeekData" :chart-options="barActivityOptions" :styles="chartStyles" chart-id="act-week" />
            </div>
          </v-col>
          <v-col cols="12" md="4">
            <div class="text-center font-weight-medium mb-1">Por medio</div>
            <div style="position: relative; height: 240px; width: 100%;">
              <Doughnut :chart-data="activityChannelData" :chart-options="doughnutOptions" :styles="chartStyles" chart-id="act-ch" />
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-row>
      <!-- Cobertura de cartera -->
      <v-col cols="12" md="7">
        <v-card>
          <v-card-title class="text-subtitle-1 font-weight-bold">
            Cartera — clientes por atender
            <v-spacer></v-spacer>
            <v-chip small class="mr-1" color="green" dark :title="`Verde: contacto reciente (hasta ${settings.green_days} días)`">{{ coverage.summary.green }} 🟢</v-chip>
            <v-chip small class="mr-1" color="amber darken-2" dark :title="`Amarillo: por contactar (${settings.green_days + 1}–${settings.yellow_days} días)`">{{ coverage.summary.yellow }} 🟡</v-chip>
            <v-chip small class="mr-1" color="red" dark :title="`Rojo: atrasado, atender ya (sobre ${settings.yellow_days} días)`">{{ coverage.summary.red }} 🔴</v-chip>
            <v-chip small color="grey" dark title="Blanco: sin contacto registrado">{{ coverage.summary.gray }} ⚪</v-chip>
          </v-card-title>
          <div class="d-flex align-center px-4 pb-1">
            <v-btn-toggle v-model="carteraFilter" dense mandatory color="primary" class="cartera-toggle">
              <v-btn small value="priorizados">Priorizados</v-btn>
              <v-btn small value="all">Todos</v-btn>
            </v-btn-toggle>
            <v-select
              v-model="carteraClass" :items="classificationItems" label="Clasificación"
              clearable dense hide-details outlined class="ml-3" style="max-width: 220px"
            ></v-select>
            <v-spacer></v-spacer>
            <span class="text-caption text--secondary">{{ coverageRowsShown.length }} clientes</span>
          </div>
          <v-data-table
            :headers="coverageHeaders" :items="coverageRowsShown" :items-per-page="10" dense
            sort-by="days_since" :sort-desc="true" must-sort
            :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
          >
            <template v-slot:item.semaforo="{ item }">
              <v-icon :color="semColor(item.semaforo)">mdi-circle</v-icon>
            </template>
            <template v-slot:item.days_since="{ item }">
              <span :class="item.semaforo === 'red' ? 'red--text font-weight-bold' : ''">
                {{ item.days_since === null ? 'Nunca' : item.days_since + ' días' }}
              </span>
            </template>
            <template v-slot:item.potential="{ item }">
              <span class="font-weight-medium">{{ item.potential ? money(item.potential) : '—' }}</span>
            </template>
          </v-data-table>
        </v-card>
      </v-col>

      <!-- Embudo -->
      <v-col cols="12" md="5">
        <v-card>
          <v-card-title class="text-subtitle-1 font-weight-bold">Embudo comercial</v-card-title>
          <v-card-text>
            <div v-for="s in funnel.stages" :key="s.stage" class="mb-2">
              <div class="d-flex justify-space-between text-body-2">
                <span>{{ s.label }}</span>
                <span class="font-weight-medium">{{ s.count }} · {{ money(s.amount) }}</span>
              </div>
              <v-progress-linear :value="funnelPct(s.count)" :color="stageColor(s.stage)" height="10" rounded></v-progress-linear>
            </div>
            <v-divider class="my-3"></v-divider>
            <div class="d-flex justify-space-between">
              <span class="text-body-2">Tasa de conversión</span>
              <span class="font-weight-bold">{{ funnel.conversion_rate }}%</span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Tablero de gerencia: Real vs Plan -->
    <v-card class="mt-2">
      <v-card-title class="text-subtitle-1 font-weight-bold">
        Tablero de gerencia — Real vs Plan
        <v-spacer></v-spacer>
        <span class="text-body-2 text--secondary">Rentabilidad real acumulada: <b>{{ money(pvr.totals.real_profit) }}</b></span>
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="4" v-for="c in gerenciaBars" :key="c.key">
            <div class="text-center font-weight-medium mb-1">{{ c.title }}</div>
            <div style="position: relative; height: 240px; width: 100%;">
              <Bar :chart-data="c.data" :chart-options="chartOptions" :styles="chartStyles" :chart-id="'g-' + c.key" />
            </div>
            <div class="d-flex justify-space-around mt-1 text-caption">
              <span>Plan <b>{{ money(c.plan) }}</b></span>
              <span>Real <b>{{ money(c.real) }}</b></span>
              <span :class="c.diff < 0 ? 'red--text' : 'green--text'">Dif <b>{{ money(c.diff) }}</b></span>
            </div>
          </v-col>
          <v-col cols="12" md="4">
            <div class="text-center font-weight-medium mb-1">Cumplimiento acumulado (ventas)</div>
            <div style="position: relative; height: 240px; width: 100%;">
              <LineChart :chart-data="cumulativeData" :chart-options="lineOptions" :styles="chartStyles" chart-id="g-cum" />
            </div>
            <div class="text-center text-caption mt-1">Cumplimiento: <b>{{ compliancePct }}%</b> del plan</div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Diálogo: registrar contacto -->
    <v-dialog v-model="contactDialog" max-width="480" persistent>
      <v-card>
        <v-card-title>Registrar contacto</v-card-title>
        <v-card-text>
          <v-autocomplete v-model="contactForm.customer" :items="customers" item-text="name" item-value="id" label="Cliente" placeholder="Escribe para buscar…" outlined dense clearable no-data-text="Sin clientes — agrégalos en la sección Clientes"></v-autocomplete>
          <v-text-field v-model="contactForm.salesperson" label="Vendedor" outlined dense></v-text-field>
          <v-text-field v-model="contactForm.date" label="Fecha" type="date" outlined dense></v-text-field>
          <v-select v-model="contactForm.channel" :items="channels" item-text="text" item-value="value" label="Medio" outlined dense></v-select>
          <v-text-field v-model="contactForm.result" label="Resultado" outlined dense></v-text-field>
          <v-checkbox v-model="contactForm.created_opportunity" label="¿Generó oportunidad?" dense hide-details></v-checkbox>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="contactDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="saving" @click="saveContact">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo: registrar negocio / cierre -->
    <v-dialog v-model="dealDialog" max-width="480" persistent>
      <v-card>
        <v-card-title>Registrar negocio</v-card-title>
        <v-card-text>
          <v-autocomplete v-model="dealForm.customer" :items="customers" item-text="name" item-value="id" label="Cliente" placeholder="Escribe para buscar…" outlined dense clearable no-data-text="Sin clientes — agrégalos en la sección Clientes"></v-autocomplete>
          <v-autocomplete v-model="dealForm.product" :items="products" item-text="name" item-value="id" label="Producto" placeholder="Escribe para buscar…" outlined dense clearable></v-autocomplete>
          <v-text-field v-model="dealForm.salesperson" label="Vendedor" outlined dense></v-text-field>
          <v-text-field v-model.number="dealForm.amount" label="Monto ($)" type="number" outlined dense></v-text-field>
          <v-select v-model="dealForm.stage" :items="stages" item-text="text" item-value="value" label="Etapa" outlined dense></v-select>
          <v-text-field v-if="dealForm.stage === 'won' || dealForm.stage === 'lost'" v-model="dealForm.close_date" label="Fecha de cierre" type="date" outlined dense></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dealDialog = false">Cancelar</v-btn>
          <v-btn color="teal" dark :loading="saving" @click="saveDeal">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo: costos reales mensuales -->
    <v-dialog v-model="costDialog" max-width="420" persistent>
      <v-card>
        <v-card-title>Costos reales {{ costYear }}</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="6" v-for="(row, i) in costForm" :key="i">
              <v-text-field v-model.number="row.amount" :label="monthNames[i]" type="number" outlined dense hide-details></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="costDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="saving" @click="saveRealCosts">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo: parámetros (admin) -->
    <v-dialog v-model="paramsDialog" max-width="460" persistent>
      <v-card>
        <v-card-title>Parámetros de seguimiento</v-card-title>
        <v-card-text>
          <div class="text-caption text--secondary mb-2">Semáforo — días desde el último contacto</div>
          <v-text-field v-model.number="settingsForm.green_days" label="Verde hasta (días)" type="number" outlined dense suffix="días" prepend-inner-icon="mdi-circle" color="green"></v-text-field>
          <v-text-field v-model.number="settingsForm.yellow_days" label="Amarillo hasta (días)" type="number" outlined dense suffix="días"></v-text-field>
          <div class="text-caption text--secondary">Sobre {{ settingsForm.yellow_days }} días → 🔴 rojo (atender ya).</div>
          <v-divider class="my-3"></v-divider>
          <v-text-field v-model.number="settingsForm.weekly_contact_goal" label="Meta de contactos por semana" type="number" outlined dense></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="paramsDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="saving" @click="saveParams">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { fmtMoneyMM } from '@/utils/customVueFilters'
import { Bar, Line as LineChart, Doughnut } from 'vue-chartjs/legacy'
import {
  Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale,
  PointElement, LineElement, ArcElement,
} from 'chart.js'

import PageHeader from '@/components/common/PageHeader.vue'

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, PointElement, LineElement, ArcElement)

const PALETTE = ['#5575DF', '#19BFA7', '#FF7D6A', '#F4B740', '#8E7CC3', '#B0BEC5']

export default {
  name: 'SeguimientoView',
  components: { Bar, LineChart, Doughnut, PageHeader },
  data() {
    return {
      coverage: { rows: [], summary: { green: 0, yellow: 0, red: 0, gray: 0 } },
      funnel: { stages: [], conversion_rate: 0, contacts: 0, won: 0, won_amount: 0 },
      pvr: { months: [], totals: {} },
      activity: { by_week: [], avg_week: 0, by_channel: [] },
      customers: [],
      products: [],
      salespeopleItems: [],
      salesperson: null,
      settings: { green_days: 15, yellow_days: 30, weekly_contact_goal: 40 },
      settingsForm: {},
      paramsDialog: false,
      saving: false,
      contactDialog: false,
      dealDialog: false,
      costDialog: false,
      contactForm: {},
      dealForm: {},
      costForm: [],
      costYear: new Date().getFullYear(),
      monthNames: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
      channels: [
        { text: 'Teléfono', value: 'phone' }, { text: 'WhatsApp', value: 'whatsapp' },
        { text: 'Reunión presencial', value: 'meeting' }, { text: 'Email', value: 'email' }, { text: 'Otro', value: 'other' },
      ],
      stages: [
        { text: 'Prospecto', value: 'prospect' }, { text: 'Contactado', value: 'contacted' },
        { text: 'Cotizado', value: 'quoted' }, { text: 'Negociación', value: 'negotiation' },
        { text: 'Ganado', value: 'won' }, { text: 'Perdido', value: 'lost' },
      ],
      carteraFilter: 'priorizados',
      carteraClass: null,
      coverageHeaders: [
        { text: '', value: 'semaforo', sortable: false, width: 40 },
        { text: 'Cliente', value: 'customer' },
        { text: 'Días sin contacto', value: 'days_since',
          sort: (a, b) => (a === null ? 1e9 : a) - (b === null ? 1e9 : b) },
        { text: 'Potencial', value: 'potential', align: 'end',
          sort: (a, b) => (a || 0) - (b || 0) },
        { text: 'Comuna', value: 'commune' },
        { text: 'Clasificación', value: 'classification' },
      ],
      chartOptions: {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { position: 'bottom' } },
        scales: { y: { beginAtZero: true } },
      },
      lineOptions: {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { position: 'bottom' } },
        scales: { y: { beginAtZero: true } },
      },
      barActivityOptions: {
        responsive: true, maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { position: 'bottom' },
          tooltip: {
            callbacks: {
              // Al pasar el mouse: además del conteo, muestra qué empresas.
              afterBody: (items) => {
                const lines = []
                items.forEach(it => {
                  const cl = it.dataset.clients && it.dataset.clients[it.dataIndex]
                  if (cl && cl.length) lines.push(it.dataset.label + ': ' + cl.join(', '))
                })
                return lines.length ? ['', ...lines] : []
              },
            },
          },
        },
        scales: {
          x: { stacked: true },
          y: { stacked: true, beginAtZero: true, grace: '10%', ticks: { precision: 0 } },
        },
      },
      doughnutOptions: {
        responsive: true, maintainAspectRatio: false,
        plugins: {
          legend: { position: 'right' },
          tooltip: {
            callbacks: {
              // Muestra las empresas contactadas por ese medio.
              afterLabel: (ctx) => {
                const cl = ctx.dataset.clients && ctx.dataset.clients[ctx.dataIndex]
                return cl && cl.length ? cl.map(x => '  ' + x) : []
              },
            },
          },
        },
      },
      // vue-chartjs envuelve el canvas en un div; sin este alto queda en 400px
      // por defecto y desborda el contenedor. Con height 100% respeta el contenedor.
      chartStyles: { position: 'relative', height: '100%', width: '100%' },
    }
  },
  computed: {
    isAdmin() {
      const ctx = this.$store.state.app && this.$store.state.app.membershipContext
      const role = ctx && ctx.role
      return role === 'OWNER' || role === 'ADMIN'
    },
    kpis() {
      const t = this.pvr.totals || {}
      return [
        { label: 'Contactos', value: this.funnel.contacts, color: '' },
        { label: 'Oportunidades', value: this.openDeals, color: '' },
        { label: 'Ganado', value: this.money(this.funnel.won_amount), color: 'green--text' },
        { label: 'Conversión', value: this.funnel.conversion_rate + '%', color: '' },
        { label: 'Venta real', value: this.money(t.real_sales), color: '' },
        { label: 'Rent. real', value: this.money(t.real_profit), color: '' },
      ]
    },
    openDeals() {
      return (this.funnel.stages || [])
        .filter(s => ['prospect', 'contacted', 'quoted', 'negotiation'].includes(s.stage))
        .reduce((a, s) => a + s.count, 0)
    },
    gerenciaBars() {
      const t = this.pvr.totals || {}
      return [
        { key: 'sales', title: 'Ventas', color: '#5575DF', planKey: 'planned_sales', realKey: 'real_sales', plan: t.planned_sales, real: t.real_sales, diff: t.diff_sales },
        { key: 'profit', title: 'Rentabilidad', color: '#19BFA7', planKey: 'planned_profit', realKey: 'real_profit', plan: t.planned_profit, real: t.real_profit, diff: t.diff_profit },
      ].map(c => ({ ...c, data: this.buildBar(c.planKey, c.realKey, c.color) }))
    },
    cumulativeData() {
      const months = this.pvr.months || []
      let pAcc = 0, rAcc = 0
      const plan = [], real = []
      months.forEach(m => { pAcc += m.planned_sales; rAcc += m.real_sales; plan.push(pAcc); real.push(rAcc) })
      return {
        labels: months.map(m => m.month.slice(0, 3)),
        datasets: [
          { label: 'Plan acum.', data: plan, borderColor: '#B0BEC5', backgroundColor: '#B0BEC5', tension: 0.3, fill: false },
          { label: 'Real acum.', data: real, borderColor: '#5575DF', backgroundColor: '#5575DF', tension: 0.3, fill: false },
        ],
      }
    },
    compliancePct() {
      const t = this.pvr.totals || {}
      return t.planned_sales ? Math.round((t.real_sales / t.planned_sales) * 100) : 0
    },
    activityWeekData() {
      const weeks = this.activity.weeks || []
      const series = this.activity.series || []
      // Una barra apilada por medio (color distinto + leyenda), con las empresas
      // guardadas en `clients` para el tooltip.
      // Sin la línea de Meta: forzaba el eje hasta el valor de la meta (ej. 40) y
      // dejaba las barras minúsculas. Ahora el eje auto-escala a los contactos reales.
      const datasets = series.map((s, i) => ({
        label: s.label,
        data: s.data,
        clients: s.clients,
        backgroundColor: PALETTE[i % PALETTE.length],
        stack: 'contactos',
        maxBarThickness: 48,
      }))
      return { labels: weeks, datasets }
    },
    activityChannelData() {
      const c = this.activity.by_channel || []
      // Traduce el código de canal (phone, whatsapp, ...) a su etiqueta en
      // español; si llega un código desconocido, muestra el código tal cual.
      const channelLabel = code => {
        const found = this.channels.find(ch => ch.value === code)
        return found ? found.text : code
      }
      return {
        labels: c.map(x => channelLabel(x.channel)),
        datasets: [{ data: c.map(x => x.count), clients: c.map(x => x.clients), backgroundColor: PALETTE }],
      }
    },
    // Filtro de la Cartera: "Priorizados" = clientes con potencial comercial (>0)
    // en algún caso; "Todos" = la cartera completa.
    coverageRowsShown() {
      let rows = (this.coverage.rows) || []
      if (this.carteraFilter !== 'all') {
        const priorizados = rows.filter(r => (r.potential || 0) > 0)
        rows = priorizados.length ? priorizados : rows
      }
      if (this.carteraClass) rows = rows.filter(r => r.classification === this.carteraClass)
      return rows
    },
    classificationItems() {
      const set = new Set((this.coverage.rows || []).map(r => r.classification).filter(Boolean))
      return Array.from(set).sort()
    },
  },
  async mounted() {
    const st = await this.getSeguimientoSettings()
    if (st.data) this.settings = st.data
    await this.loadAll()
    const sp = await this.getSalespeople()
    this.salespeopleItems = (sp.data) || []
    const c = await this.listCustomers({ params: { page_size: 500 } })
    this.customers = (c.data && (c.data.results || c.data)) || []
    const p = await this.listProducts({ params: { page_size: 500 } })
    this.products = (p.data && (p.data.results || p.data)) || []
    this.triggerResize()
  },
  methods: {
    ...mapActions('commercial_projection', ['getCoverage', 'getFunnel', 'getPlanVsReal', 'getActivity', 'createSalesContact', 'createDeal', 'getSalespeople', 'getRealCosts', 'setRealCosts', 'getSeguimientoSettings', 'updateSeguimientoSettings']),
    ...mapActions('customer', ['listCustomers']),
    ...mapActions('product', ['listProducts']),
    money(v) {
      if (v == null) return '$0'
      return fmtMoneyMM(v)
    },
    semColor(s) { return { green: 'green', yellow: 'amber darken-2', red: 'red', gray: 'grey' }[s] || 'grey' },
    stageColor(s) { return { won: 'green', lost: 'red', negotiation: 'teal', quoted: 'blue', contacted: 'indigo', prospect: 'grey' }[s] || 'primary' },
    funnelPct(count) {
      const max = Math.max(1, ...(this.funnel.stages || []).map(s => s.count))
      return (count / max) * 100
    },
    buildBar(planKey, realKey, colorReal) {
      const months = this.pvr.months || []
      return {
        labels: months.map(m => m.month.slice(0, 3)),
        datasets: [
          { label: 'Plan', data: months.map(m => m[planKey]), backgroundColor: '#B0BEC5' },
          { label: 'Real', data: months.map(m => m[realKey]), backgroundColor: colorReal },
        ],
      }
    },
    // Los gráficos responsive de Chart.js pueden montarse antes de que su
    // contenedor tenga tamaño (quedan en el alto por defecto de 400px y desbordan);
    // un resize los obliga a recalcular al alto real del contenedor.
    triggerResize() {
      this.$nextTick(() => setTimeout(() => window.dispatchEvent(new Event('resize')), 350))
    },
    async loadAll() {
      await this.loadActivity()
      const pvr = await this.getPlanVsReal({})
      if (pvr.data) this.pvr = pvr.data
      this.triggerResize()
    },
    async loadActivity() {
      const params = this.salesperson ? { salesperson: this.salesperson } : {}
      const [cov, fun, act] = await Promise.all([this.getCoverage(params), this.getFunnel(params), this.getActivity(params)])
      if (cov.data) this.coverage = cov.data
      if (fun.data) this.funnel = fun.data
      if (act.data) this.activity = act.data
    },
    today() { return new Date().toISOString().slice(0, 10) },
    openContact() {
      this.contactForm = { customer: null, salesperson: this.salesperson || '', date: this.today(), channel: 'phone', result: '', created_opportunity: false }
      this.contactDialog = true
    },
    openDeal() {
      this.dealForm = { customer: null, product: null, salesperson: this.salesperson || '', amount: 0, stage: 'prospect', close_date: null }
      this.dealDialog = true
    },
    openParams() {
      this.settingsForm = { ...this.settings }
      this.paramsDialog = true
    },
    async saveParams() {
      this.saving = true
      const { data, error } = await this.updateSeguimientoSettings({ data: this.settingsForm })
      this.saving = false
      if (error) {
        const msg = (error.data && (error.data.detail || Object.values(error.data)[0])) || 'No se pudieron guardar los parámetros'
        this.$toast ? this.$toast.error(String(msg)) : alert(String(msg))
        return
      }
      this.settings = data
      this.paramsDialog = false
      this.$toast && this.$toast.success('Parámetros actualizados')
      this.loadActivity()
    },
    async openRealCosts() {
      const r = await this.getRealCosts({ year: this.costYear })
      this.costForm = (r.data && r.data.costs) ? r.data.costs.map(c => ({ ...c })) : this.monthNames.map((_, i) => ({ month: i + 1, amount: 0 }))
      this.costDialog = true
    },
    async saveContact() {
      this.saving = true
      const { error } = await this.createSalesContact({ data: this.contactForm })
      this.saving = false
      if (error) { this.$toast && this.$toast.error('No se pudo guardar el contacto'); return }
      this.contactDialog = false
      this.$toast && this.$toast.success('Contacto registrado')
      this.loadAll()
    },
    async saveDeal() {
      this.saving = true
      const payload = { ...this.dealForm }
      if (payload.stage !== 'won' && payload.stage !== 'lost') payload.close_date = null
      const { error } = await this.createDeal({ data: payload })
      this.saving = false
      if (error) { this.$toast && this.$toast.error('No se pudo guardar el negocio'); return }
      this.dealDialog = false
      this.$toast && this.$toast.success('Negocio registrado')
      this.loadAll()
    },
    async saveRealCosts() {
      this.saving = true
      const { error } = await this.setRealCosts({ data: { year: this.costYear, costs: this.costForm } })
      this.saving = false
      if (error) { this.$toast && this.$toast.error('No se pudieron guardar los costos'); return }
      this.costDialog = false
      this.$toast && this.$toast.success('Costos reales actualizados')
      const pvr = await this.getPlanVsReal({})
      if (pvr.data) this.pvr = pvr.data
    },
  },
}
</script>
